import React, { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { eur } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ImageUploader } from '@/components/ImageUploader';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { mergeCategoryLabels } from '@/lib/categoryLabels';

const BUILT_IN_CATS = [
  { key: 'size', label: 'Taglie' },
  { key: 'base', label: 'Basi' },
  { key: 'protein', label: 'Proteine' },
  { key: 'topping', label: 'Topping' },
  { key: 'sauce', label: 'Salse' },
  { key: 'extra', label: 'Extra' },
  { key: 'beverage', label: 'Bevande' },
];
const BUILT_IN_KEYS = BUILT_IN_CATS.map(c => c.key);

const emptyIng = {
  category: 'base', name: '', description: '', price: 0, image_url: '',
  active: true, sort_order: 0,
  max_proteins: null, max_toppings: null, max_sauces: null,
  max_bases: null, max_extras: null, max_total: null, max_qty_per_item: 1,
};

export default function AdminMenu() {
  const [tab, setTab] = useState('size');
  const [ingredients, setIngredients] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [labels, setLabels] = useState(mergeCategoryLabels(null));
  const [edit, setEdit] = useState(null);
  const [editSig, setEditSig] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // { kind, id, name }
  const [newCatDialog, setNewCatDialog] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  const load = () => {
    api.get('/ingredients?active_only=false').then(r => setIngredients(r.data));
    api.get('/signature-bowls?active_only=false').then(r => setSignatures(r.data));
    api.get('/settings').then(r => setLabels(mergeCategoryLabels(r.data?.category_labels))).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // Compute custom categories: any ingredient.category not in BUILT_IN_KEYS
  const customCategories = useMemo(() => {
    const set = new Set();
    ingredients.forEach(i => { if (!BUILT_IN_KEYS.includes(i.category)) set.add(i.category); });
    return Array.from(set).sort();
  }, [ingredients]);

  const allCats = useMemo(() => [
    ...BUILT_IN_CATS,
    ...customCategories.map(c => ({ key: c, label: c.charAt(0).toUpperCase() + c.slice(1), custom: true })),
    { key: 'signature', label: 'Poke firmate' },
  ], [customCategories]);

  const saveIngredient = async () => {
    if (!edit.category || !edit.category.trim()) return toast.error('Seleziona una categoria');
    if (!edit.name || !edit.name.trim()) return toast.error('Inserisci un nome');
    try {
      const payload = { ...edit, category: edit.category.trim().toLowerCase() };
      if (edit.id) await api.patch(`/admin/ingredients/${edit.id}`, payload);
      else         await api.post('/admin/ingredients', payload);
      toast.success('Salvato');
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore salvataggio'); }
  };

  const saveSignature = async () => {
    try {
      if (editSig.id) await api.patch(`/admin/signature-bowls/${editSig.id}`, editSig);
      else            await api.post('/admin/signature-bowls', editSig);
      toast.success('Salvato');
      setEditSig(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore salvataggio'); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      if (confirmDel.kind === 'ingredient') {
        await api.delete(`/admin/ingredients/${confirmDel.id}`);
        setIngredients(prev => prev.filter(x => x.id !== confirmDel.id));
      } else {
        await api.delete(`/admin/signature-bowls/${confirmDel.id}`);
        setSignatures(prev => prev.filter(x => x.id !== confirmDel.id));
      }
      toast.success(`"${confirmDel.name}" eliminata`);
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const addNewCategory = () => {
    const c = newCatName.trim().toLowerCase();
    if (!c) return toast.error('Nome categoria richiesto');
    if (['signature'].includes(c)) return toast.error('Nome riservato');
    setEdit({ ...emptyIng, category: c });
    setTab(c);
    setNewCatDialog(false);
    setNewCatName('');
  };

  return (
    <AdminLayout testid="admin-menu-page">
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Menu</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Gestione menu</h1>
        </div>
        <Button variant="secondary" className="rounded-xl" onClick={() => setNewCatDialog(true)} data-testid="admin-new-category-button">
          <Plus size={14} className="mr-1"/>Nuova categoria
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1 bg-[--brand-surface-2] p-1 rounded-xl h-auto">
          {allCats.map(c => (
            <TabsTrigger key={c.key} value={c.key} className="rounded-lg">
              {c.key === 'signature' ? c.label : (labels[c.key] || c.label)}
              {c.custom && <Badge className="ml-1 bg-[--brand-accent]/15 text-[--brand-accent] border-[--brand-accent]/30 text-[9px]">custom</Badge>}
            </TabsTrigger>
          ))}
        </TabsList>

        {allCats.filter(c => c.key !== 'signature').map(cat => (
          <TabsContent key={cat.key} value={cat.key} className="mt-4">
            <div className="flex justify-between mb-3">
              <div className="text-sm text-[--brand-muted]">{ingredients.filter(i => i.category === cat.key).length} elementi</div>
              <Button className="rounded-xl bg-[--brand-primary]" onClick={() => setEdit({ ...emptyIng, category: cat.key })} data-testid="admin-add-ingredient-button">
                <Plus size={14} className="mr-1"/>Aggiungi
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {ingredients.filter(i => i.category === cat.key).sort((a,b)=>a.sort_order-b.sort_order).map(ing => (
                <div key={ing.id} className="rounded-2xl bg-white border border-[--brand-border] p-3 shadow-[var(--shadow-sm)]">
                  {ing.image_url && <img src={ing.image_url} alt={ing.name} className="aspect-[4/3] rounded-xl object-cover w-full mb-2"/>}
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold">{ing.name}</div>
                    <div className="tabular-nums font-semibold text-[--brand-primary]">{ing.price > 0 ? '+' : ''}{eur(ing.price)}</div>
                  </div>
                  {ing.description && <div className="text-xs text-[--brand-muted] mt-1 line-clamp-2">{ing.description}</div>}
                  {!ing.active && <div className="text-xs text-[--brand-danger] mt-1">Non attivo</div>}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="secondary" className="rounded-lg flex-1" onClick={() => setEdit(ing)}><Pencil size={12} className="mr-1"/>Modifica</Button>
                    <Button size="sm" variant="destructive" className="rounded-lg" onClick={() => setConfirmDel({ kind: 'ingredient', id: ing.id, name: ing.name })} data-testid={`admin-delete-ingredient-${ing.id}`}><Trash2 size={12}/></Button>
                  </div>
                </div>
              ))}
              {ingredients.filter(i => i.category === cat.key).length === 0 && (
                <div className="col-span-full rounded-2xl bg-white border border-dashed border-[--brand-border] p-8 text-center text-[--brand-muted]">
                  Nessun elemento in <b>{labels[cat.key] || cat.label}</b>. Clicca su <b>Aggiungi</b> per iniziare.
                </div>
              )}
            </div>
          </TabsContent>
        ))}

        <TabsContent value="signature" className="mt-4">
          <div className="flex justify-between mb-3">
            <div className="text-sm text-[--brand-muted]">{signatures.length} poke firmate</div>
            <Button className="rounded-xl bg-[--brand-primary]" onClick={() => setEditSig({ name: '', description: '', price: 12.9, image_url: '', tags: [], active: true, featured: false })} data-testid="admin-add-signature-button">
              <Plus size={14} className="mr-1"/>Aggiungi
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {signatures.map(s => (
              <div key={s.id} className="rounded-2xl bg-white border border-[--brand-border] p-3 shadow-[var(--shadow-sm)]" data-testid="admin-signature-card">
                {s.image_url && <img src={s.image_url} alt={s.name} className="aspect-[4/3] rounded-xl object-cover w-full mb-2"/>}
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold">{s.name}</div>
                  <div className="tabular-nums font-semibold text-[--brand-primary]">{eur(s.price)}</div>
                </div>
                {s.description && <div className="text-xs text-[--brand-muted] mt-1 line-clamp-2">{s.description}</div>}
                {s.tags && s.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {s.tags.map((t,i) => <Badge key={i} className="bg-[--brand-primary]/10 text-[--brand-primary] border-[--brand-primary]/30 text-[10px]">{t}</Badge>)}
                  </div>
                )}
                {!s.active && <div className="text-xs text-[--brand-danger] mt-1">Non attivo</div>}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="secondary" className="rounded-lg flex-1" onClick={() => setEditSig(s)} data-testid={`admin-signature-edit-${s.id}`}><Pencil size={12} className="mr-1"/>Modifica</Button>
                  <Button size="sm" variant="destructive" className="rounded-lg" onClick={() => setConfirmDel({ kind: 'signature', id: s.id, name: s.name })} data-testid={`admin-signature-delete-${s.id}`}><Trash2 size={12}/></Button>
                </div>
              </div>
            ))}
            {signatures.length === 0 && (
              <div className="col-span-full rounded-2xl bg-white border border-dashed border-[--brand-border] p-8 text-center text-[--brand-muted]">
                Nessuna poke firmata. Clicca <b>Aggiungi</b>.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Ingredient edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{edit?.id ? 'Modifica elemento' : 'Nuovo elemento'}</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div>
                <Label>Categoria</Label>
                <Input list="cat-suggestions" value={edit.category} onChange={e => setEdit({...edit, category: e.target.value})} className="rounded-xl" data-testid="ingredient-category-input"/>
                <datalist id="cat-suggestions">
                  {[...BUILT_IN_KEYS, ...customCategories].map(k => <option key={k} value={k}/>)}
                </datalist>
                <p className="text-xs text-[--brand-muted] mt-1">Puoi scrivere qualsiasi categoria (es. <b>fritture</b>, <b>antipasti</b>, <b>dolci</b>). Verrà creata automaticamente.</p>
              </div>
              <div><Label>Nome</Label><Input value={edit.name} onChange={e => setEdit({...edit, name: e.target.value})} className="rounded-xl"/></div>
              <div><Label>Descrizione</Label><Textarea value={edit.description || ''} onChange={e => setEdit({...edit, description: e.target.value})} className="rounded-xl" rows={2}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prezzo (€)</Label><Input type="number" step="0.10" value={edit.price} onChange={e => setEdit({...edit, price: parseFloat(e.target.value) || 0})} className="rounded-xl"/></div>
                <div><Label>Ordine</Label><Input type="number" value={edit.sort_order || 0} onChange={e => setEdit({...edit, sort_order: parseInt(e.target.value) || 0})} className="rounded-xl"/></div>
              </div>
              <ImageUploader value={edit.image_url || ''} onChange={(url) => setEdit({...edit, image_url: url})} testid="ingredient-image-uploader"/>
              {edit.category === 'size' && (
                <div className="rounded-xl bg-[--brand-surface-2] p-3 space-y-3">
                  <div className="text-xs font-semibold text-[--brand-muted] uppercase tracking-widest">Limiti per questa taglia</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div><Label className="text-xs">Max basi</Label><Input type="number" min="0" value={edit.max_bases ?? ''} onChange={e => setEdit({...edit, max_bases: e.target.value === '' ? null : parseInt(e.target.value)})} className="rounded-xl" placeholder="∞"/></div>
                    <div><Label className="text-xs">Max proteine</Label><Input type="number" min="0" value={edit.max_proteins ?? ''} onChange={e => setEdit({...edit, max_proteins: e.target.value === '' ? null : parseInt(e.target.value)})} className="rounded-xl" placeholder="∞"/></div>
                    <div><Label className="text-xs">Max topping</Label><Input type="number" min="0" value={edit.max_toppings ?? ''} onChange={e => setEdit({...edit, max_toppings: e.target.value === '' ? null : parseInt(e.target.value)})} className="rounded-xl" placeholder="∞"/></div>
                    <div><Label className="text-xs">Max salse</Label><Input type="number" min="0" value={edit.max_sauces ?? ''} onChange={e => setEdit({...edit, max_sauces: e.target.value === '' ? null : parseInt(e.target.value)})} className="rounded-xl" placeholder="∞"/></div>
                    <div><Label className="text-xs">Max extra</Label><Input type="number" min="0" value={edit.max_extras ?? ''} onChange={e => setEdit({...edit, max_extras: e.target.value === '' ? null : parseInt(e.target.value)})} className="rounded-xl" placeholder="∞"/></div>
                    <div><Label className="text-xs">Max totale</Label><Input type="number" min="0" value={edit.max_total ?? ''} onChange={e => setEdit({...edit, max_total: e.target.value === '' ? null : parseInt(e.target.value)})} className="rounded-xl" placeholder="∞"/></div>
                  </div>
                  <div>
                    <Label className="text-xs">Ripetizioni max per singolo ingrediente</Label>
                    <Input type="number" min="1" max="10" value={edit.max_qty_per_item ?? 1} onChange={e => setEdit({...edit, max_qty_per_item: parseInt(e.target.value) || 1})} className="rounded-xl w-32"/>
                    <p className="text-xs text-[--brand-muted] mt-1">Quante volte si può aggiungere lo stesso ingrediente.</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({...edit, active: v})}/><Label>Attivo</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={saveIngredient} className="rounded-xl bg-[--brand-primary]" data-testid="admin-save-ingredient-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature edit dialog */}
      <Dialog open={!!editSig} onOpenChange={(o) => !o && setEditSig(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editSig?.id ? 'Modifica poke firmata' : 'Nuova poke firmata'}</DialogTitle></DialogHeader>
          {editSig && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={editSig.name} onChange={e => setEditSig({...editSig, name: e.target.value})} className="rounded-xl"/></div>
              <div><Label>Descrizione</Label><Textarea value={editSig.description || ''} onChange={e => setEditSig({...editSig, description: e.target.value})} className="rounded-xl" rows={3}/></div>
              <div><Label>Prezzo (€)</Label><Input type="number" step="0.10" value={editSig.price} onChange={e => setEditSig({...editSig, price: parseFloat(e.target.value) || 0})} className="rounded-xl w-40"/></div>
              <ImageUploader value={editSig.image_url || ''} onChange={(url) => setEditSig({...editSig, image_url: url})} testid="signature-image-uploader"/>
              <div>
                <Label>Etichette (separate da virgola)</Label>
                <Input
                  value={(editSig.tags || []).join(', ')}
                  onChange={e => setEditSig({...editSig, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})}
                  placeholder="Es. Vegan, Piccante, Novità, Best seller"
                  className="rounded-xl"
                  data-testid="signature-tags-input"
                />
                {editSig.tags && editSig.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {editSig.tags.map((t, i) => <Badge key={i} className="bg-[--brand-primary]/10 text-[--brand-primary] border-[--brand-primary]/30">{t}</Badge>)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm"><Switch checked={editSig.active} onCheckedChange={(v) => setEditSig({...editSig, active: v})}/> Attivo</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={editSig.featured} onCheckedChange={(v) => setEditSig({...editSig, featured: v})}/> In evidenza</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditSig(null)}>Annulla</Button>
            <Button onClick={saveSignature} className="rounded-xl bg-[--brand-primary]" data-testid="admin-save-signature-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete dialog (robust: no window.confirm) */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="max-w-md" data-testid="admin-confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[--brand-danger]"><AlertTriangle size={18}/> Confermi l'eliminazione?</DialogTitle>
            <DialogDescription>Stai per eliminare definitivamente <b>&ldquo;{confirmDel?.name}&rdquo;</b>. L'azione non è reversibile.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)}>Annulla</Button>
            <Button variant="destructive" onClick={doDelete} className="rounded-xl" data-testid="admin-confirm-delete-button">
              <Trash2 size={14} className="mr-1"/>Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New category dialog */}
      <Dialog open={newCatDialog} onOpenChange={setNewCatDialog}>
        <DialogContent className="max-w-md" data-testid="admin-new-category-dialog">
          <DialogHeader>
            <DialogTitle>Nuova categoria</DialogTitle>
            <DialogDescription>Crea una nuova sezione di menu (es. "Fritture", "Antipasti", "Dolci").</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome categoria</Label>
              <Input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="Es. Fritture" className="rounded-xl" autoFocus data-testid="admin-new-category-input"
                     onKeyDown={e => e.key === 'Enter' && addNewCategory()}/>
              <p className="text-xs text-[--brand-muted] mt-1">La categoria appariràcome nuova tab. Puoi aggiungere subito il primo elemento.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewCatDialog(false)}>Annulla</Button>
            <Button onClick={addNewCategory} className="rounded-xl bg-[--brand-primary]" data-testid="admin-new-category-create"><Plus size={14} className="mr-1"/>Crea e aggiungi elemento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
